// The renderer shell: owns the value store, exposes the imperative handle,
// provides FieldContext, and renders the field tree (NOT a <form> — hosts wrap
// it). Reuses the pure logic layers: createDefaultValue/seedValue to seed,
// isFieldVisible per field (via FieldNode), validateFieldAtPath on blur, and
// validateDefinition for handle.validate() / validateOnMount.

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import type {
  DynamicFormDefinition,
  DynamicFormHandle,
  DynamicFormValue,
  UploadAdapter,
  ValidationErrors,
} from './types'
import { getFieldValue, pruneHiddenValues, stripRowKeys } from './value'
import { collectFieldStates } from './introspection'
import { validateDefinition, validateFieldAtPath } from './validation'
import type { ResolutionLevel } from './visibility'
import { createFormStore } from './store'
import { fieldIdFor } from './ids'
import { seedValue } from './seed'
import { FieldNode } from './FieldNode'
import {
  FieldContext,
  type ClassNameOverrides,
  type DataSourceResolver,
  type DynamicFormMode,
  type EnhancedMultiSelectAdapter,
  type EnhancedSingleSelectAdapter,
  type FieldComponentOverrides,
  type FieldContextValue,
  type LabelSuffixRenderer,
  type RichTextAdapter,
  type WidgetComponentOverrides,
} from './rendererContext'

export interface DynamicFormRendererProps {
  definition: DynamicFormDefinition
  /** Seeds the internal store. NOT a controlled prop — later changes are ignored. */
  defaultValue?: DynamicFormValue
  /** Notification only; fired (stripped of row keys) whenever a value changes. */
  onChange?: (value: DynamicFormValue) => void
  onValidationChange?: (errors: ValidationErrors) => void
  mode?: DynamicFormMode
  /** Run a full validation sweep once on mount. */
  validateOnMount?: boolean
  classNames?: ClassNameOverrides
  /** Per-type component overrides; keys are field types. */
  fieldComponents?: FieldComponentOverrides
  /** Widget renderers for passthrough markup widgets; keys are `config.widget` names. */
  widgetComponents?: WidgetComponentOverrides
  /** Resolves async option sets for data-source-backed enum fields. */
  dataSourceResolver?: DataSourceResolver
  /** Persists files for `upload` fields. Without it, upload fields are read-only. */
  uploadAdapter?: UploadAdapter
  /**
   * Form-level flag (mirrors alpaca's `enable_rich_text`): upgrades `textarea`
   * fields to rich editors. Requires `richTextAdapter`; without it, no effect.
   */
  enableRichText?: boolean
  /** Rich-text editor component used when `enableRichText` is set. */
  richTextAdapter?: RichTextAdapter
  /**
   * Select2-style multi-select control for `multi_select` fields. Without it
   * (or with a field's `config.plain_select`) the native `<select multiple>`
   * renders instead.
   */
  enhancedSelectAdapter?: EnhancedMultiSelectAdapter
  /**
   * Custom dropdown for single `select` fields. Without it (or with a field's
   * `config.plain_select`) the native `<select>` renders instead.
   */
  enhancedSingleSelectAdapter?: EnhancedSingleSelectAdapter
  /**
   * Optional host-supplied renderer of a small badge/pill placed after each
   * field label. Receives the field's path so it can vary content per field
   * — e.g. a "Double-check" pill on auto-prefilled fields. Return null to
   * render nothing.
   */
  labelSuffix?: LabelSuffixRenderer
}

export const DynamicFormRenderer = forwardRef<DynamicFormHandle, DynamicFormRendererProps>(
  function DynamicFormRenderer(props, ref) {
    const {
      definition,
      defaultValue,
      onChange,
      onValidationChange,
      mode = 'edit',
      validateOnMount = false,
      classNames,
      fieldComponents,
      widgetComponents,
      dataSourceResolver,
      uploadAdapter,
      enableRichText,
      richTextAdapter,
      enhancedSelectAdapter,
      enhancedSingleSelectAdapter,
      labelSuffix,
    } = props

    // Store is created once; `definition`/`defaultValue` changes after mount do
    // not reseed (uncontrolled by design).
    const storeRef = useRef<ReturnType<typeof createFormStore>>(undefined)
    if (storeRef.current == null) {
      storeRef.current = createFormStore(seedValue(definition, defaultValue))
    }
    const store = storeRef.current

    const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot)
    const [errors, setErrors] = useState<ValidationErrors>({})
    const containerRef = useRef<HTMLDivElement>(null)

    // Keep callbacks current without resubscribing.
    const onChangeRef = useRef(onChange)
    onChangeRef.current = onChange
    const onValidationChangeRef = useRef(onValidationChange)
    onValidationChangeRef.current = onValidationChange

    // Emit onChange on every store mutation (not on mount).
    useEffect(
      () =>
        store.subscribe(() => {
          onChangeRef.current?.(stripRowKeys(store.getSnapshot()))
        }),
      [store],
    )

    const runValidate = useCallback((): ValidationErrors => {
      const next = validateDefinition(definition, store.getSnapshot())
      setErrors(next)
      onValidationChangeRef.current?.(next)
      return next
    }, [definition, store])

    const notifyBlur = useCallback(
      (path: string) => {
        const scoped = validateFieldAtPath(definition, store.getSnapshot(), path)
        setErrors((prev) => {
          const next = mergeScopedErrors(prev, path, scoped)
          onValidationChangeRef.current?.(next)
          return next
        })
      },
      [definition, store],
    )

    useImperativeHandle(
      ref,
      (): DynamicFormHandle => ({
        getValue: () => stripRowKeys(pruneHiddenValues(definition, store.getSnapshot())),
        setValue: (value) => store.setValue(seedValue(definition, value)),
        setFieldValue: (path, value) => store.setFieldValue(path, value),
        validate: () => runValidate(),
        focusField: (path) => focusControl(containerRef.current, path),
        reset: () => store.reset(),
        // Read-only queries: each reads the live snapshot at call time and
        // delegates to the pure layers. The validation reads deliberately
        // bypass setErrors/onValidationChange (unlike runValidate) so a query
        // never flashes the red error UI.
        getFields: () => collectFieldStates(definition, store.getSnapshot()),
        getVisibleFields: () =>
          collectFieldStates(definition, store.getSnapshot()).filter((f) => f.visible),
        getFieldValue: (path) => getFieldValue(stripRowKeys(store.getSnapshot()), path),
        getValidation: () => validateDefinition(definition, store.getSnapshot()),
        getFieldValidation: (path) =>
          validateFieldAtPath(definition, store.getSnapshot(), path)[path] ?? [],
      }),
      [store, definition, runValidate],
    )

    // validateOnMount sweep, once.
    useEffect(() => {
      if (validateOnMount) runValidate()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const setFieldValue = useCallback(
      (path: string, value: unknown) => store.setFieldValue(path, value),
      [store],
    )

    const contextValue = useMemo<FieldContextValue>(
      () => ({
        mode,
        classNames: classNames ?? {},
        errors,
        fieldComponents: fieldComponents ?? {},
        widgetComponents: widgetComponents ?? {},
        dataSourceResolver,
        uploadAdapter,
        enableRichText,
        richTextAdapter,
        enhancedSelectAdapter,
        enhancedSingleSelectAdapter,
        labelSuffix,
        setFieldValue,
        notifyBlur,
      }),
      [mode, classNames, errors, fieldComponents, widgetComponents, dataSourceResolver, uploadAdapter, enableRichText, richTextAdapter, enhancedSelectAdapter, enhancedSingleSelectAdapter, labelSuffix, setFieldValue, notifyBlur],
    )

    const rootLevels: ResolutionLevel[] = [{ fields: definition.fields, value: snapshot }]

    return (
      <div ref={containerRef} className="dynamic-form-renderer">
        <FieldContext.Provider value={contextValue}>
          {definition.fields.map((field) => (
            <FieldNode
              key={field.key}
              field={field}
              levels={rootLevels}
              valueObj={snapshot}
              pathPrefix=""
            />
          ))}
        </FieldContext.Provider>
      </div>
    )
  },
)

/** Drop `path` and its descendants from `prev`, then layer in the fresh scoped errors. */
function mergeScopedErrors(
  prev: ValidationErrors,
  path: string,
  scoped: ValidationErrors,
): ValidationErrors {
  const next: ValidationErrors = {}
  for (const [key, value] of Object.entries(prev)) {
    if (key === path || key.startsWith(`${path}.`) || key.startsWith(`${path}[`)) continue
    next[key] = value
  }
  return { ...next, ...scoped }
}

const FOCUSABLE_SELECTOR =
  'input:not([type="hidden"]), select, textarea, button, [tabindex]:not([tabindex="-1"])'

function focusControl(container: HTMLElement | null, path: string): void {
  const id = fieldIdFor(path)
  const root: ParentNode = container ?? document
  const escaped =
    typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(id) : id
  const el = root.querySelector<HTMLElement>(`#${escaped}`)
  if (el == null) return
  // Grouped controls (radio / multi-checkbox / scalar-list / button toggles)
  // carry the id on a non-focusable wrapper; focus the first focusable control
  // inside instead so focusField always lands on something a user can act on.
  if (el.matches(FOCUSABLE_SELECTOR)) {
    el.focus()
    return
  }
  const focusable = el.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)
  ;(focusable ?? el).focus()
}
